import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { mobile, tablet, ScrollWrap, Main, Sidebar, HeaderOverlay, SidebarSection, TopGradient, TopGradientSidebar, ShadowSoftener, ShadowSoftenerGradient } from '~styles/global'
import { graphql } from 'gatsby'
import DataViewer from '~utils/DataViewer'
import Image from '~components/Image'
import Video from '~components/Video'
import SanityLink from '~components/SanityLink'
import RichText from '~components/RichText'
import { Cross } from '~components/Svg'
import { AnimatePresence, motion } from 'framer-motion'
import { useSiteState } from '~context/siteContext'
import useSiteSettings from '~utils/useSiteSettings'

const ProjectDesktop = ({ data }) => {
	const { project } = data
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const [ siteState ] = useSiteState()
	const sidebarRef = useRef()
	const mainRef = useRef()
	const { acknowledgement } = useSiteSettings()
	const [mainScrolled, setMainScrolled] = useState(0)
	const [sidebarScrolled, setSidebarScrolled] = useState(0)
	
	useEffect(() => {
		if(mainRef.current){
			mainRef.current.addEventListener('scroll', e => {
				setMainScrolled(e.target.scrollTop)
			})
		}
		if(sidebarRef.current){
			sidebarRef.current.addEventListener('scroll', e => {
				setSidebarScrolled(e.target.scrollTop)
			})
		}
	}, [])

	return (
		<Wrap>
			<HeaderOverlay headerHeight={siteState.headerHeight}/>
			<ScrollWrap headerHeight={siteState.headerHeight}>
				<StyledMain open={!sidebarOpen} ref={mainRef}>
					<TopGradient 
						scrolled={mainScrolled} 
						headerHeight={siteState.headerHeight} 
						open={!sidebarOpen}
					/> 
					{project.mediaRows?.map(row => (
						<Row key={row._key}>
							{row.media.map(media =>(
								<React.Fragment key={media._key}>
									{media._type === 'imageWithAlt' &&
									<MediaContainer>
										<Image image={media}/>
									</MediaContainer>
									}
									{media._type === 'videoEmbed' &&
									<MediaContainer>
										<StyledVideo src={media.video?.asset?.url}/>
									</MediaContainer>
									}
								</React.Fragment>
							))}
						</Row>
					))}
					<Footer>
						<UpButton onClick={() => mainRef.current?.scrollTo({
							top: 0,
							left: 0,
							behavior: 'smooth'
						})}>↑</UpButton>
						<Acknowledgement>
							<RichText content={acknowledgement}/>
						</Acknowledgement>
					</Footer>
				</StyledMain>
				<ShadowSoftener
					transition={{
						type: 'spring',
						stiffness: 200,
						damping: 28
					}}
					animate={{
						width: (sidebarOpen) ? 'calc(34vw - 15px)' : '4vw',
					}}
				>
					<ShadowSoftenerGradient />
				</ShadowSoftener>	
				<Sidebar 
					ref={sidebarRef} 
					open={sidebarOpen}
					initial={false}
					transition={{
						type: 'spring',
						stiffness: 200,
						damping: 28
					}}
					animate={{
						width: (sidebarOpen) ? 'calc(34vw - 15px)' : '4vw',
						padding: sidebarOpen ? '1.6vw 2vw' : '0vw',
					}}>
					<TopGradientSidebar scrolled={sidebarScrolled} headerHeight={siteState.headerHeight}/> 
					<AnimatePresence exitBeforeEnter>	
						{!sidebarOpen && 
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.45, delay: 0.4 }}
							>
								<OpenButton onClick={() => setSidebarOpen(true)}>
									<ProjectTitleMobile 
										className='h4'
									>
										{project?.title} 
										<Open>+</Open>
									</ProjectTitleMobile> 
								</OpenButton>
							</motion.div>
						}
						{sidebarOpen && 
						<SidebarInner
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.45, delay: 0.6 }}
						>
							<SidebarInfo>
								<ProjectTitle>{project?.title} 
									<CloseButton onClick={() => setSidebarOpen(false)}><Close /></CloseButton>
								</ProjectTitle>
								{project?.infoRows.map(row => (
									<SidebarRow key={row.title} link={row.link}>
										<span>{row.title}</span>
										<span>
											{row?.subtitle?.split(',').map((name, index) => (
												<SubtitleRow key={index}>{name.trim()}</SubtitleRow>
											))}
										</span>
									</SidebarRow>
								))}
							</SidebarInfo>
							{project.text &&
								<SidebarText>
									<RichText content={project.text}/>
								</SidebarText>
							}
							{project?.categories?.length > 0 &&
								<Categories>
									{project?.categories?.map(category => (
										<Category className='h4' key={category?.title} link={category?.link}>{category?.title}</Category>
									))}
								</Categories>
							}
							{project.awards?.length > 0 &&
								<Awards>
									<h4>Awards</h4>
									{project?.awards?.map(row => (
										<SidebarRow key={row.title} link={row.link}>
											<span>{row.title}</span>
											<span>{row.subtitle}</span>
										</SidebarRow>
									))}
								</Awards>
							}
							{project.creativeTeam?.length > 0 &&
								<CreativeTeam>
									<h4>Creative Team</h4>
									{project?.creativeTeam?.map(row => (
										<SidebarRow key={row.title} link={row.link}>
											<span>{row.title}</span>
											<span>
												{row?.subtitle?.split(',').map((name, index) => (
													<SubtitleRow key={index}>{name.trim()}</SubtitleRow>
												))}
											</span>
										</SidebarRow>
									))}
								</CreativeTeam>
							}
							<SidebarFooter>
								<UpButton onClick={() => sidebarRef.current?.scrollTo({
									top: 0,
									left: 0,
									behavior: 'smooth'
								})}>↑</UpButton>
								<Copyright>Evi-O.Studio &copy; {new Date().getFullYear()}</Copyright>
							</SidebarFooter>
						</SidebarInner>
						}
					</AnimatePresence>
				</Sidebar>
				<DataViewer data={project} name="content"/>
			</ScrollWrap>
		</Wrap>
	)
}

const Wrap = styled.div`
	
`

const SidebarInner = styled(motion.div)`
	position: relative;
	z-index: 100;
`

const StyledMain = styled(Main)`
	${mobile}{
		opacity: ${props => props.open ? '1' : '0'};
		padding: ${props => props.open ? '4vw' : '0vw'};
		transition: opacity 0.1s, padding 0.2s;
	}
`

const Footer = styled.footer`
	display: flex;
	justify-content: space-between;
	padding: 50vh 0 2vw;
	align-items: flex-end;
	${mobile}{
		flex-wrap: wrap;
		padding: 20vh 0 1.5vw;
	}
`

const Copyright = styled.div`
	text-transform: uppercase;
	font-size: 13px;
	margin-bottom: 0.5vw;
	margin-left: 3em;
	${mobile}{
		order: 3;
	}
`

const Acknowledgement = styled.div`
	text-transform: uppercase;
	font-size: 13px;
	margin-bottom: 0.5vw;
	margin-left: 3em;
	max-width: 500px;
	${mobile}{
		margin-left: 0;
		order: 1;
		margin-bottom: 3.5vw;
		max-width: none;
	}
`

const UpButton = styled.button`
	font-size: 3vw;
	${mobile}{
		order: 2;
	}
`

const Row = styled.div`
	display: flex;
	margin-bottom: 20px;	
	:first-of-type{
		margin-top: 20px;
	}
	${mobile}{
		flex-wrap: wrap;
	}
`
const MediaContainer = styled.div`
	margin-right: 20px;
	flex: 1;
	:last-of-type{
		margin-right: 0px;
	}
	${mobile}{
		width: 100%;
		margin-right: 0;
		flex: 100%;
		margin-bottom: 20px;
	}
`
const StyledVideo = styled(Video)`
	width: 100%;
	display: block;
`
const SidebarRow = styled(SanityLink)`
	display: flex;
	border-top: 1px solid #000;
	padding: 0.18em 0 0.05em;
	justify-content: space-between;
	>span:first-of-type{
		padding-right: 1.5vw;
	}
	>span:last-of-type{
		white-space: nowrap;
	}
`
const SubtitleRow = styled.div`
	text-align: right;
`
const SidebarInfo = styled(SidebarSection)`
	margin-top: -0.30em;
	${mobile}{
		margin-top: 0;
	}
`
const ProjectTitle = styled.h4`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
const OpenButton = styled.button`
	transform: translate(1.25vw, 0px);
	margin-top: calc(1.6vw);
	position: relative;
	z-index: 10;
	${mobile}{
		transform: translate(-1.6vw, 0px);
		margin-top: 23px;
	}
`
const ProjectTitleMobile = styled.div`
	writing-mode: vertical-rl;
	text-orientation: mixed;
	transform-origin: 0 0;
	white-space: nowrap;
	display: flex;
	position: relative;
`
const Open = styled.div`
	margin-top: 1.1em;
`
const CloseButton = styled.button`
	width: 10px;
	height: 10px;
	display: flex;
	align-items: center;
`
const Close = styled(Cross)`
	width: 0.45rem;
`
const SidebarText = styled.div`
	margin-bottom: 2.5em;
`
const Awards = styled(SidebarSection)`
	margin-bottom: 2.5em;
`
const CreativeTeam = styled(SidebarSection)`
	
`
const SidebarFooter = styled.div`
	padding-top: 20vw;
	margin-top: auto;
	display: flex;
	align-items: end;
	justify-content: space-between;
	margin-bottom: 0;
	${mobile}{
		flex-wrap: wrap;
		padding: 20vh 0 1.5vw;
	}
`
const Categories = styled(SidebarSection)`
	margin-bottom: 4vw;
`
const Category = styled(SanityLink)`
	padding: 0.2em 0;
	/* :not(:first-of-type){ */
		border-bottom: 1px solid;
	/* } */
`

ProjectDesktop.propTypes = {
	className: PropTypes.string,
	data: PropTypes.object,
}

export default ProjectDesktop